// src/components/inventory/InventoryStatisticsWidget.js
// Statistics dashboard widget for inventory overview
//
// --- UPDATE (Inventory MOW+ v2) ----------------------------------------------------------
//   • Unknown values show "—" instead of an invented 0 / 100 % (Stock Health defaulted to 100 %).
//   • Stock Health is the FlowSense AI health score when the overview is passed (`intel`).
//   • Two FlowSense cards when the overview is available: Out of stock and Re-order value.
//   • Live: a value that changes (WebSocket refresh) pulses briefly; thin bar while refreshing
//     (`isRefreshing`, which the page already passes).
// --- END UPDATE ---------------------------------------------------------------------------

import React from 'react';
import {
  Box,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Icon,
  useColorModeValue,
  Skeleton,
  HStack,
  Text,
  Progress, // --- UPDATE
  Tooltip, // --- UPDATE
} from '@chakra-ui/react';
import {
  MdInventory,
  MdTrendingUp,
  MdWarning,
  MdAttachMoney,
  MdShoppingCart,
} from 'react-icons/md';
import { MdAutoAwesome, MdRemoveShoppingCart } from 'react-icons/md'; // --- UPDATE
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

// --- UPDATE: "—" for unknown values
const known = (v) => v !== null && v !== undefined && !Number.isNaN(Number(v));
const show = (v) => (known(v) ? v : '—');
// --- END UPDATE

// UPDATE: Removed internal fetching/mock data. Now relies purely on passed props.
const InventoryStatisticsWidget = ({ stats, isLoading, intel = null, isRefreshing = false }) => { // --- UPDATE: + intel, isRefreshing

  // Theme colors
  const statBg = useColorModeValue('white', 'gray.700');
  const statBorderColor = useColorModeValue('gray.100', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
  const iconBgAlpha = useColorModeValue('50', '900'); // --- UPDATE: icon tile readable in dark mode

  const formatCurrency = (value) => {
    if (!known(value)) return '—'; // --- UPDATE: was $0 for unknown
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0); // Failsafe fallback
  };

  const safeStats = stats || {}; // Failsafe against undefined props
  // --- UPDATE: FlowSense AI numbers when available
  const t = intel?.totals || null;
  const health = known(safeStats.stockHealth) ? safeStats.stockHealth : known(intel?.score) ? intel.score : null;
  const healthFromAi = !known(safeStats.stockHealth) && known(intel?.score);
  // --- END UPDATE

  const statsConfig = [
    {
      label: 'Total Items',
      value: show(safeStats.totalItems), // --- UPDATE: was || 0
      icon: MdInventory,
      color: 'blue',
      change: null, // Hardcoded changes removed to reflect real data
      helpText: 'Active SKUs',
    },
    {
      label: 'Low Stock',
      value: show(safeStats.lowStockItems), // --- UPDATE
      icon: MdWarning,
      color: 'orange',
      change: null,
      helpText: t ? 'Below the FlowSense re-order point' : 'Below threshold', // --- UPDATE
      isWarning: (safeStats.lowStockItems || 0) > 0,
    },
    {
      label: 'Total Value',
      value: formatCurrency(safeStats.totalValue),
      icon: MdAttachMoney,
      color: 'green',
      change: null,
      helpText: 'Total inventory worth',
    },
    {
      label: 'Stock Health',
      value: known(health) ? `${health}%` : '—', // --- UPDATE: was `|| 100` (invented 100 %)
      icon: healthFromAi ? MdAutoAwesome : MdTrendingUp, // --- UPDATE
      color: 'purple',
      change: null,
      helpText: healthFromAi ? 'FlowSense AI health score' : 'Optimal supply', // --- UPDATE
      isWarning: known(health) && health < 60, // --- UPDATE
    },
    {
      label: 'Need Reorder',
      value: show(safeStats.needsReorder), // --- UPDATE
      icon: MdShoppingCart,
      color: 'red',
      change: null,
      helpText: 'Critically low',
      isWarning: (safeStats.needsReorder || 0) > 0,
    },
    // --- UPDATE (MOW+ v2): FlowSense cards
    ...(t ? [
      { label: 'Out of Stock', value: show(t.stockouts), icon: MdRemoveShoppingCart, color: 'red', change: null, helpText: 'Zero on hand now', isWarning: (t.stockouts || 0) > 0, ai: true },
      { label: 'Re-order Value', value: formatCurrency(t.reorder_value), icon: MdAutoAwesome, color: 'teal', change: null, helpText: 'FlowSense suggested buy', ai: true },
    ] : []),
    // --- END UPDATE
  ];

  // --- UPDATE: grid fits 5 or 7 cards
  const cols = statsConfig.length > 5 ? { base: 1, sm: 2, md: 4, xl: 7 } : { base: 1, sm: 2, md: 3, lg: 5 };

  if (isLoading) {
    return (
      <SimpleGrid columns={cols} spacing={4}>
        {[...Array(statsConfig.length)].map((_, index) => (
          <Skeleton key={index} height="120px" borderRadius="2xl" />
        ))}
      </SimpleGrid>
    );
  }

  return (
    <Box position="relative" data-testid="inventory-stats">
    {/* --- UPDATE: live refresh bar */}
    {isRefreshing && <Progress size="xs" isIndeterminate colorScheme="teal" borderRadius="full" position="absolute" top="-8px" left={0} right={0} />}
    <SimpleGrid columns={cols} spacing={4}>
      {statsConfig.map((stat, index) => (
        <MotionBox
          key={stat.label}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: index * 0.1 }}
        >
          <Stat
            px={4}
            py={5}
            bg={statBg}
            borderRadius="2xl"
            borderWidth="2px"
            borderColor={stat.isWarning ? `${stat.color}.300` : statBorderColor}
            boxShadow="md"
            position="relative"
            overflow="hidden"
            _hover={{
              boxShadow: 'xl',
              borderColor: `${stat.color}.400`,
              transform: 'translateY(-2px)',
            }}
            transition="all 0.2s"
          >
            {/* Background gradient decoration */}
            <Box
              position="absolute"
              top={0}
              right={0}
              w="100px"
              h="100px"
              bgGradient={`linear(to-br, ${stat.color}.100, transparent)`}
              opacity={0.3}
              borderRadius="full"
              transform="translate(30%, -30%)"
            />

            <HStack justify="space-between" mb={3}>
              <Box
                p={2}
                borderRadius="lg"
                bg={`${stat.color}.${iconBgAlpha}`} // --- UPDATE: was .50 in both modes
              >
                <Icon
                  as={stat.icon}
                  color={`${stat.color}.500`}
                  boxSize={6}
                />
              </Box>

              {stat.change && (
                <HStack spacing={1}>
                  <StatArrow type={stat.changeType} />
                  <Text
                    fontSize="xs"
                    fontWeight="600"
                    color={stat.changeType === 'increase' ? 'green.500' : 'red.500'}
                  >
                    {stat.change}
                  </Text>
                </HStack>
              )}
              {/* --- UPDATE: FlowSense badge */}
              {stat.ai && <Tooltip hasArrow label="Computed by FlowSense AI / ML from live stock and usage"><Text fontSize="2xs" fontWeight="800" color="teal.400" cursor="help">AI</Text></Tooltip>}
            </HStack>

            <StatLabel
              fontSize="xs"
              color={mutedTextColor}
              fontWeight="600"
              textTransform="uppercase"
            >
              {stat.label}
            </StatLabel>

            {/* --- UPDATE: key on the value so a live change pulses */}
            <MotionBox key={String(stat.value)} initial={{ scale: 1.12, opacity: 0.4 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.45 }} transformOrigin="left center">
            <StatNumber
              fontSize="2xl"
              fontWeight="800"
              color={stat.isWarning ? `${stat.color}.500` : textColor}
              mb={1}
            >
              {stat.value}
            </StatNumber>
            </MotionBox>

            <StatHelpText fontSize="xs" mb={0} color={mutedTextColor}>
              {stat.helpText}
            </StatHelpText>
          </Stat>
        </MotionBox>
      ))}
    </SimpleGrid>
    </Box>
  );
};

export default InventoryStatisticsWidget;
